import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { ArrowLeft, Check, Fingerprint, LoaderCircle, Mail } from "lucide-react";
import { useReOS } from "./domain/store";

type AuthMode = "signin" | "create";
type AuthStep = "email" | "details" | "code";
type SignInMethod = "device" | "email";
type EmailCodeDestination = Readonly<{
  email: string;
  intent: AuthMode;
  name?: string;
  workspaceName?: string;
  invitationToken?: string;
}>;
type AuthOperation = "request" | "complete" | "device" | "verification" | "demo";
type TurnstileAction = "login" | "signup";
type TurnstileWindow = Window & {
  turnstile?: {
    render: (element: HTMLElement, options: Record<string, unknown>) => string;
    remove?: (widgetID: string) => void;
  };
};

const TURNSTILE_SITE_KEY = import.meta.env.VITE_REOS_TURNSTILE_SITE_KEY as string | undefined;

export function ProgressiveAuthScreen({ onOpenLocalDemo }: { onOpenLocalDemo?: () => void | Promise<void> } = {}) {
  const store = useReOS();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<AuthMode>(() => {
    const requestedMode = new URLSearchParams(window.location.search).get("mode");
    return requestedMode === "create" || window.location.pathname === "/create-account" ? "create" : "signin";
  });
  const [step, setStep] = useState<AuthStep>("email");
  const [signInMethod, setSignInMethod] = useState<SignInMethod>("device");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string>();
  const [codeDestination, setCodeDestination] = useState<EmailCodeDestination>();
  const [status, setStatus] = useState<string | undefined>(() => new URLSearchParams(window.location.search).get("verified") === "1" ? "Email verified. Sign in to continue." : undefined);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>();
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [verificationToken, setVerificationToken] = useState(() => new URLSearchParams(window.location.search).get("verifyEmailToken") ?? "");
  const [verificationComplete, setVerificationComplete] = useState(false);
  const verificationAttemptedRef = useRef(false);
  const mountedRef = useRef(true);
  const operationVersionRef = useRef(0);
  const operationRef = useRef<AuthOperation>();
  const errorID = useId();
  const deviceSupportID = useId();
  const destinationLocked = busy && operationRef.current !== "request";

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationVersionRef.current += 1;
      operationRef.current = undefined;
    };
  }, []);

  const isCurrentOperation = (version: number) => mountedRef.current && operationVersionRef.current === version;

  const beginOperation = (operation: AuthOperation) => {
    if (!mountedRef.current || operationRef.current) return undefined;
    operationRef.current = operation;
    const version = ++operationVersionRef.current;
    setBusy(true);
    setError(undefined);
    setStatus(undefined);
    return version;
  };

  const finishOperation = (version: number, challengeToken?: string) => {
    if (!isCurrentOperation(version)) return;
    operationRef.current = undefined;
    setBusy(false);
    if (challengeToken) resetTurnstile();
  };

  const invalidateDestination = () => {
    // Once verification starts, the store may commit an authenticated session.
    // Keep its destination fixed rather than presenting a misleading cancel.
    if (operationRef.current && operationRef.current !== "request") return false;
    operationVersionRef.current += 1;
    operationRef.current = undefined;
    setBusy(false);
    setCodeDestination(undefined);
    setCode("");
    setDevCode(undefined);
    setError(undefined);
    setStatus(undefined);
    return true;
  };

  const resetTurnstile = () => {
    setTurnstileToken(undefined);
    setTurnstileResetKey((current) => current + 1);
  };

  const requestCode = async (profile?: { name?: string; workspaceName?: string }) => {
    if (operationRef.current) return;
    if (store.persistenceMode !== "api") {
      setError("Email codes aren't available in this local preview.");
      return;
    }
    const destination: EmailCodeDestination = step === "code" && codeDestination
      ? codeDestination
      : {
        email: email.trim(),
        intent: mode,
        name: profile?.name ?? (mode === "create" ? name.trim() : undefined),
        workspaceName: profile?.workspaceName ?? (mode === "create" ? workspaceName.trim() || undefined : undefined),
        invitationToken: new URLSearchParams(window.location.search).get("invitation") ?? undefined
      };
    const challengeToken = turnstileToken;
    if (TURNSTILE_SITE_KEY && !challengeToken) {
      setError("Complete the security check.");
      return;
    }
    const version = beginOperation("request");
    if (version === undefined) return;
    try {
      const result = await store.requestEmailSignInCode({
        ...destination,
        turnstileToken: challengeToken
      });
      if (!isCurrentOperation(version)) return;
      if (!result.ok) throw new Error("The code could not be sent.");
      setCodeDestination(destination);
      setCode("");
      setDevCode(result.code);
      setStep("code");
      setStatus(step === "code" ? "A new code has been sent." : undefined);
    } catch (reason) {
      if (isCurrentOperation(version)) setError(authError(reason));
    } finally {
      finishOperation(version, challengeToken);
    }
  };

  const useEmailCode = () => {
    if (!invalidateDestination()) return;
    setSignInMethod("email");
    setStep("email");
    setCode("");
    setDevCode(undefined);
    setError(undefined);
    setStatus(undefined);
    if (TURNSTILE_SITE_KEY) resetTurnstile();
  };

  const returnToDeviceSignIn = () => {
    if (!invalidateDestination()) return;
    setSignInMethod("device");
    setStep("email");
    setCode("");
    setDevCode(undefined);
    setError(undefined);
    setStatus(undefined);
    if (TURNSTILE_SITE_KEY) resetTurnstile();
  };

  const signInWithDevice = async () => {
    if (operationRef.current) return;
    const challengeToken = turnstileToken;
    const version = beginOperation("device");
    if (version === undefined) return;
    try {
      if (!window.PublicKeyCredential) {
        setError("Device sign-in isn't available in this browser. Use an email code instead.");
        return;
      }
      if (TURNSTILE_SITE_KEY && !challengeToken) {
        setError("Complete the security check.");
        return;
      }
      const result = await store.createPasskeyAuthenticationOptions(undefined, challengeToken);
      if (!isCurrentOperation(version)) return;
      const credential = await startAuthentication({
        optionsJSON: result.options as Parameters<typeof startAuthentication>[0]["optionsJSON"]
      });
      if (!isCurrentOperation(version)) return;
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
      await store.completePasskeyAuthentication(result.challengeID, credential);
    } catch (reason) {
      if (isCurrentOperation(version)) setError(deviceSignInError(reason));
    } finally {
      finishOperation(version, challengeToken);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (operationRef.current) return;
    if (step === "email") {
      if (!email.trim() || !email.includes("@")) {
        setError("Enter a valid email address.");
        window.requestAnimationFrame(() => emailRef.current?.focus());
        return;
      }
      if (mode === "create") {
        setError(undefined);
        setStep("details");
      } else {
        await requestCode();
      }
      return;
    }
    if (step === "details") {
      if (!name.trim()) {
        setError("Enter your name.");
        window.requestAnimationFrame(() => nameRef.current?.focus());
        return;
      }
      await requestCode({ name: name.trim(), workspaceName: workspaceName.trim() || undefined });
      return;
    }
    if (!/^\d{6}$/.test(code.replace(/\s/g, ""))) {
      setError("Enter the 6-digit code from your email.");
      window.requestAnimationFrame(() => codeRef.current?.focus());
      return;
    }
    const destination = codeDestination;
    if (!destination || destination.email !== email.trim() || destination.intent !== mode) {
      if (!invalidateDestination()) return;
      setStep("email");
      setError("Request a new code for this email address.");
      return;
    }
    const submittedCode = code.replace(/\s/g, "");
    const version = beginOperation("complete");
    if (version === undefined) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
      await store.completeEmailSignInCode(destination.email, submittedCode, {
        offerDeviceSignIn: destination.intent === "create"
      });
    } catch (reason) {
      if (!isCurrentOperation(version)) return;
      setError(authError(reason, "code"));
      window.requestAnimationFrame(() => {
        if (!isCurrentOperation(version)) return;
        codeRef.current?.focus();
        codeRef.current?.select();
      });
    } finally {
      finishOperation(version);
    }
  };

  const restart = (nextMode = mode) => {
    if (!invalidateDestination()) return;
    if (nextMode !== mode) {
      setSignInMethod(nextMode === "signin" ? "device" : "email");
    }
    setMode(nextMode);
    const url = new URL(window.location.href);
    url.searchParams.set("mode", nextMode);
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    setStep("email");
    setCode("");
    setDevCode(undefined);
    setError(undefined);
    setStatus(undefined);
    if (TURNSTILE_SITE_KEY) resetTurnstile();
  };

  const confirmEmail = async () => {
    if (!verificationToken || operationRef.current || store.persistenceMode !== "api") return;
    verificationAttemptedRef.current = true;
    const version = beginOperation("verification");
    if (version === undefined) return;
    try {
      await store.completeEmailVerification(verificationToken);
      if (!isCurrentOperation(version)) return;
      setVerificationComplete(true);
      setVerificationToken("");
      window.history.replaceState({}, "", "/verify-email");
      try {
        localStorage.setItem("listwork:email-verified", String(Date.now()));
        const channel = new BroadcastChannel("listwork:account");
        channel.postMessage({ type: "email-verified" });
        channel.close();
      } catch {
        // Cross-tab refresh is an enhancement; verification has already completed.
      }
    } catch (reason) {
      if (isCurrentOperation(version)) setError(authError(reason, "verification"));
    } finally {
      finishOperation(version);
    }
  };

  useEffect(() => {
    if (!verificationToken || verificationAttemptedRef.current) return;
    const timeout = window.setTimeout(() => void confirmEmail(), 0);
    return () => window.clearTimeout(timeout);
  }, [verificationToken]);

  const changeEmail = (nextEmail: string) => {
    const wasRequesting = operationRef.current === "request";
    if (!invalidateDestination()) return;
    setEmail(nextEmail);
    if (wasRequesting && TURNSTILE_SITE_KEY) resetTurnstile();
  };

  const openLocalDemo = async () => {
    if (!onOpenLocalDemo) return;
    const version = beginOperation("demo");
    if (version === undefined) return;
    try {
      await onOpenLocalDemo();
    } catch {
      if (isCurrentOperation(version)) setError("The local preview couldn't be opened. Try again.");
    } finally {
      finishOperation(version);
    }
  };

  const showingDeviceSignIn = mode === "signin" && signInMethod === "device" && step === "email";
  const title = step === "code"
    ? "Check your email"
    : step === "details"
      ? "Tell us about you"
      : showingDeviceSignIn
        ? "Sign in"
      : mode === "create"
        ? "Create your account"
        : "Email sign-in";

  useEffect(() => {
    const pageTitle = store.persistenceMode !== "api"
      ? store.persistenceMode === "local_demo" ? "Local preview" : "Workspace unavailable"
      : verificationToken || verificationComplete
      ? verificationComplete ? "Email confirmed" : "Confirm email"
      : title;
    document.title = `${pageTitle} | Listwork`;
  }, [store.persistenceMode, title, verificationComplete, verificationToken]);

  if (store.persistenceMode !== "api") {
    const localDemo = store.persistenceMode === "local_demo";
    return (
      <main className="auth-screen auth-screen-progressive">
        <section className="auth-progressive-shell" aria-labelledby="auth-title">
          <div className="auth-progressive-brand" aria-label="Listwork">Listwork</div>
          <div className="auth-progressive-card auth-local-preview">
            <h1 id="auth-title" ref={headingRef} tabIndex={-1}>{localDemo ? "Local preview" : "Workspace unavailable"}</h1>
            <p className="auth-progressive-lede">{localDemo ? "Email codes and device sign-in aren't available in this local preview." : "Listwork isn't connected to your workspace. Try again in a moment."}</p>
            {error ? <p className="auth-error" role="alert">{error}</p> : null}
            {localDemo && onOpenLocalDemo ? <button className="auth-primary" type="button" disabled={busy} onClick={() => void openLocalDemo()}>{busy ? "Opening preview..." : "Open demo workspace"}</button> : null}
            {!localDemo ? <button className="auth-primary" type="button" onClick={() => window.location.reload()}>Try again</button> : null}
          </div>
        </section>
      </main>
    );
  }

  if (verificationToken || verificationComplete) {
    const verificationFailed = Boolean(error);
    const verificationLinkRejected = /invalid|expired|already (?:been )?used/i.test(error ?? "");
    return (
      <main className="auth-screen auth-screen-progressive">
        <section className="auth-progressive-shell" aria-labelledby="verify-email-title">
          <div className="auth-progressive-brand" aria-label="Listwork">Listwork</div>
          <div className="auth-progressive-card">
            <div className="auth-step-mark" aria-hidden="true">{verificationComplete ? <Check size={22} /> : verificationFailed ? <Mail size={22} /> : <LoaderCircle className="spin" size={22} />}</div>
            <h1 id="verify-email-title" tabIndex={-1}>{verificationComplete ? "Email verified" : verificationFailed ? "We couldn't verify this link" : "Verifying your email"}</h1>
            <p className="auth-progressive-lede">
              {verificationComplete
                ? "Your address is confirmed. Continue to Listwork."
                : verificationFailed
                  ? "Try the link again. If it has expired, sign in and request a new email."
                  : "This should only take a moment."}
            </p>
            {error ? <p className="auth-error" role="alert">{error}</p> : null}
            {verificationComplete ? (
              <button className="auth-primary" type="button" onClick={() => window.location.assign("/login?verified=1")}>Continue to sign in</button>
            ) : verificationFailed ? (
              <button
                className="auth-primary"
                type="button"
                onClick={verificationLinkRejected ? () => window.location.assign("/login") : () => void confirmEmail()}
                disabled={busy}
              >
                {busy ? <LoaderCircle className="spin" size={18} /> : null}
                {verificationLinkRejected ? "Return to sign in" : "Try again"}
              </button>
            ) : (
              <div className="auth-verification-status" role="status"><span>Checking your confirmation link</span></div>
            )}
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-screen auth-screen-progressive">
      <section className="auth-progressive-shell" aria-labelledby="auth-title">
        <div className="auth-progressive-brand" aria-label="Listwork">Listwork</div>
        <div className="auth-progressive-card">
          {step !== "email" || (mode === "signin" && signInMethod === "email") ? (
            <button className="auth-back" type="button" disabled={destinationLocked} onClick={step === "email" ? returnToDeviceSignIn : () => restart()}>
              <ArrowLeft size={16} aria-hidden="true" /> Back
            </button>
          ) : null}
          <div className="auth-step-mark" aria-hidden="true">
            {step === "code" ? <Mail size={22} /> : <span>{step === "details" ? "2" : "1"}</span>}
          </div>
          <h1 id="auth-title" ref={headingRef} tabIndex={-1}>{title}</h1>
          {!showingDeviceSignIn ? (
            <p className="auth-progressive-lede">
              {step === "code"
                ? <>Enter the code sent to <strong>{codeDestination?.email}</strong>.</>
                : step === "details"
                  ? "This names your workspace. You can change it later."
                  : mode === "create"
                    ? "Start with your email. Marketplace accounts are connected later."
                    : "Use the email address for your Listwork account."}
            </p>
          ) : null}

          {showingDeviceSignIn ? (
            <div className="auth-device-signin">
              <TurnstileControl key={`device-login-${turnstileResetKey}`} action="login" onToken={setTurnstileToken} />
              {error ? <p className="auth-error" id={errorID} role="alert">{error}</p> : null}
              <button className="auth-primary" type="button" onClick={() => void signInWithDevice()} disabled={busy} aria-describedby={deviceSupportID}>
                {busy ? <LoaderCircle className="spin" size={18} aria-hidden="true" /> : <Fingerprint size={18} aria-hidden="true" />}
                {busy ? "Signing in" : "Sign in with your device"}
              </button>
              <p className="auth-device-support" id={deviceSupportID}>Use Face ID, your fingerprint, or device PIN.</p>
              <div className="auth-choice-divider" aria-hidden="true"><span>or</span></div>
              <button className="auth-secondary-button" type="button" onClick={useEmailCode} disabled={busy}>
                <Mail size={18} aria-hidden="true" /> Email me a code
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              {step === "email" ? (
                <label className="auth-field">
                  <span>Email address</span>
                  <input ref={emailRef} autoFocus type="email" inputMode="email" autoComplete="email" value={email} onChange={(event) => changeEmail(event.target.value)} disabled={destinationLocked} aria-invalid={Boolean(error) || undefined} aria-describedby={error ? errorID : undefined} />
                </label>
              ) : null}
              {step === "details" ? (
                <div className="auth-field-stack">
                  <label className="auth-field">
                    <span>Your name</span>
                    <input ref={nameRef} autoFocus autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} disabled={busy} aria-invalid={error === "Enter your name." || undefined} />
                  </label>
                  <label className="auth-field">
                    <span>Business name <small>Optional</small></span>
                    <input autoComplete="organization" value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} disabled={busy} placeholder="For example, Rose & Thread" />
                  </label>
                </div>
              ) : null}
              {step === "code" ? (
                <label className="auth-field auth-code-field">
                  <span>6-digit code</span>
                  <input ref={codeRef} autoFocus type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} disabled={busy} aria-invalid={Boolean(error) || undefined} aria-describedby={error ? errorID : undefined} />
                </label>
              ) : null}

              <TurnstileControl
                key={`${mode}-${turnstileResetKey}`}
                action={mode === "create" ? "signup" : "login"}
                onToken={setTurnstileToken}
              />
              {status ? <p className="auth-status" role="status"><Check size={15} aria-hidden="true" />{status}</p> : null}
              {devCode ? <p className="auth-dev-code">Local test code: <strong>{devCode}</strong></p> : null}
              {error ? <p className="auth-error" id={errorID} role="alert">{error}</p> : null}
              <button className="auth-primary" type="submit" disabled={busy}>
                {busy ? <LoaderCircle className="spin" size={18} aria-hidden="true" /> : null}
                {busy ? (step === "code" && operationRef.current === "complete" ? "Signing in..." : "Sending code...") : step === "code" ? (codeDestination?.intent === "create" ? "Create account" : "Sign in") : mode === "signin" ? "Email me a code" : "Continue"}
              </button>
            </form>
          )}

          {step === "code" ? (
            <div className="auth-secondary-actions">
              <button type="button" onClick={() => requestCode()} disabled={busy}>Send a new code</button>
              <button type="button" onClick={() => restart()} disabled={destinationLocked}>Change email</button>
            </div>
          ) : step === "email" ? (
            <p className="auth-mode-switch">
              {mode === "signin" ? "New to Listwork?" : "Already have an account?"}{" "}
              <button type="button" onClick={() => restart(mode === "signin" ? "create" : "signin")} disabled={destinationLocked}>
                {mode === "signin" ? "Create account" : "Sign in"}
              </button>
            </p>
          ) : null}
        </div>
        <p className="auth-progressive-footnote">Secure sign-in without a password. We will never ask for your marketplace password.</p>
      </section>
    </main>
  );
}

export function DeviceSignInOnboardingScreen() {
  const store = useReOS();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    document.title = "Set up device sign-in | Listwork";
    headingRef.current?.focus();
  }, []);

  const setUpDeviceSignIn = async () => {
    if (!window.PublicKeyCredential) {
      setError("Device sign-in is not available in this browser. You can set it up later from Settings.");
      return;
    }

    setBusy(true);
    setError(undefined);
    try {
      const registration = await store.createAccountPasskeyRegistrationOptions();
      const credential = await startRegistration({
        optionsJSON: registration.options as Parameters<typeof startRegistration>[0]["optionsJSON"]
      });
      await store.completeAccountPasskeyRegistration(registration.challengeID, credential);
      store.finishDeviceSignInSetup();
    } catch (reason) {
      setError(deviceSetupError(reason));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-screen auth-screen-progressive">
      <section className="auth-progressive-shell" aria-labelledby="device-sign-in-setup-heading">
        <div className="auth-progressive-brand" aria-label="Listwork">Listwork</div>
        <div className="auth-progressive-card">
          <h1 id="device-sign-in-setup-heading" ref={headingRef} tabIndex={-1}>Sign in faster next time</h1>
          <div className="auth-device-signin auth-device-setup">
            <p className="auth-device-support">Use Face ID, your fingerprint, or device PIN.</p>
            {error ? <p className="auth-error" role="alert">{error}</p> : null}
            <button className="auth-primary" type="button" disabled={busy} onClick={setUpDeviceSignIn}>
              {busy ? <LoaderCircle aria-hidden="true" className="auth-spinner" /> : <Fingerprint aria-hidden="true" />}
              {busy ? "Setting up..." : "Set up on this device"}
            </button>
            <button className="auth-secondary-button" type="button" disabled={busy} onClick={store.finishDeviceSignInSetup}>
              Not now
            </button>
          </div>
        </div>
        <p className="auth-progressive-footnote">Your email code will still work if you use another device.</p>
      </section>
    </main>
  );
}

function deviceSetupError(reason: unknown) {
  if (reason instanceof DOMException && reason.name === "NotAllowedError") {
    return "Setup was cancelled. Try again, or choose Not now.";
  }
  if (reason instanceof DOMException && reason.name === "InvalidStateError") {
    return "This device is already set up. Choose Not now to continue.";
  }
  if (reason instanceof Error && /network|fetch|connection/i.test(reason.message)) {
    return "We couldn't finish setup. Check your connection and try again.";
  }
  return "We couldn't set up this device. Try again, or choose Not now.";
}

function TurnstileControl({ action, onToken }: { action: TurnstileAction; onToken: (token: string | undefined) => void }) {
  const siteKey = TURNSTILE_SITE_KEY;
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!siteKey || !host.current) return;
    let cancelled = false;
    let widgetID: string | undefined;
    const currentWindow = window as TurnstileWindow;
    const render = () => {
      if (cancelled || !host.current || !currentWindow.turnstile) return;
      widgetID = currentWindow.turnstile.render(host.current, {
        sitekey: siteKey,
        appearance: "interaction-only",
        action,
        theme: "light",
        callback: (token: string) => { if (!cancelled) onToken(token); },
        "expired-callback": () => { if (!cancelled) onToken(undefined); },
        "error-callback": () => { if (!cancelled) onToken(undefined); }
      });
    };
    if (currentWindow.turnstile) render();
    else {
      const existing = document.querySelector<HTMLScriptElement>('script[data-listwork-turnstile]');
      const script = existing ?? document.createElement("script");
      if (!existing) {
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.dataset.listworkTurnstile = "true";
        document.head.appendChild(script);
      }
      script.addEventListener("load", render, { once: true });
    }
    return () => {
      cancelled = true;
      onToken(undefined);
      if (widgetID) currentWindow.turnstile?.remove?.(widgetID);
    };
  }, [action, onToken, siteKey]);
  return siteKey ? <div className="auth-turnstile" ref={host} role="group" aria-label="Security check" /> : null;
}

function authError(reason: unknown, context: "general" | "code" | "verification" = "general") {
  const message = reason instanceof Error ? reason.message : "Listwork could not complete that step.";
  if (/rate|too many/i.test(message)) return "Too many attempts. Wait a moment, then try again.";
  if (context === "code" && /expired|invalid|incorrect/i.test(message)) return "That code is incorrect or has expired. Request a new code and try again.";
  if (context === "verification" && /expired|invalid/i.test(message)) return "That confirmation link is invalid or has expired. Request a new one from Listwork.";
  if (/workspace.*approved|private beta|access/i.test(message)) return message;
  return "Listwork could not complete that step. Check your connection and try again.";
}

function deviceSignInError(reason: unknown) {
  const message = reason instanceof Error ? reason.message : "";
  if (reason instanceof DOMException && reason.name === "NotAllowedError") {
    return "Device sign-in wasn't completed. Try again or use an email code.";
  }
  if (/not found|no passkey|unknown credential|not registered/i.test(message)) {
    return "No saved device sign-in was found. Use an email code instead.";
  }
  if (/network|fetch|reach|connect|unavailable/i.test(message)) {
    return "Listwork couldn't reach your workspace. Check your connection and try again.";
  }
  return "Device sign-in didn't finish. Try again or use an email code.";
}
